import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Minus, ArrowRight } from "lucide-react";
import { packages, currencies, formatPrice } from "@/content/pricing";
import { severity } from "@/content/general";
import { countries, matrix, matrixTopics } from "@/content/countries";
import { Reveal } from "@/components/common/Reveal";
import { serviceIcons, IconTile } from "@/components/common/icons";

// ---------- Currency ----------
const KEY = "rf-currency";
export const useCurrency = () => {
  const [cur, setCur] = useState(() => { try { return localStorage.getItem(KEY) || "INR"; } catch { return "INR"; } });
  const set = (c) => { setCur(c); try { localStorage.setItem(KEY, c); } catch { /* storage unavailable */ } };
  return [cur, set];
};

export const CurrencySwitch = ({ cur, setCur, dark = false }) => (
  <div role="radiogroup" aria-label="Display currency" className={`inline-flex rounded-sm border p-1 ${dark ? "border-white/20" : "border-ink/15 bg-white"}`} data-testid="currency-switch">
    {Object.keys(currencies).map((c) => (
      <button key={c} type="button" role="radio" aria-checked={cur === c} onClick={() => setCur(c)} data-testid={`currency-${c.toLowerCase()}`}
        className={`min-w-[64px] rounded-[2px] px-4 py-2 font-mono text-xs font-semibold tracking-wider transition-colors ${cur === c ? "bg-teal text-white" : dark ? "text-slate-300 hover:text-white" : "text-slate-600 hover:text-ink"}`}>
        {c}
      </button>
    ))}
  </div>
);

export const PriceCard = ({ pkg, cur, index = 0 }) => (
  <Reveal delay={index * 0.06} className={`relative flex h-full flex-col bg-white p-7 xl:p-6 ${pkg.flagship ? "ring-2 ring-teal" : ""}`} data-testid={`package-${pkg.id}`}>
    {pkg.flagship && <span className="absolute -top-3 left-7 bg-teal px-2.5 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.18em] text-white">Flagship</span>}
    <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">0{index + 1}</p>
    <h3 className="mt-3 text-xl font-extrabold">{pkg.name}</h3>
    <p className="mt-2 min-h-[84px] text-sm leading-relaxed text-slate-600 line-clamp-4">{pkg.audience}</p>
    <div className="mt-6 border-y border-line py-5">
      <p className="text-xs font-medium text-slate-500">Starting at</p>
      <p className="mt-1 font-display text-3xl font-extrabold tabular-nums xl:text-[1.65rem]" data-testid={`price-${pkg.id}`}>
        {formatPrice(pkg, cur)}
      </p>
      {pkg.period && <p className="text-sm font-semibold text-slate-500">per {pkg.period}</p>}
      <p className="mt-1 text-xs text-slate-500">{cur === "USD" ? "Reference price · quote depends on scope" : "Plus applicable taxes"}</p>
    </div>
    <ul className="mt-6 flex-1 space-y-2.5">
      {pkg.includes.map((i) => <li key={i} className="flex gap-2.5 text-sm text-slate-700"><Check className="mt-0.5 h-4 w-4 flex-none text-teal" aria-hidden />{i}</li>)}
    </ul>
    <p className="mt-6 min-h-[48px] text-xs leading-relaxed text-slate-500 line-clamp-3">{pkg.timeline}</p>
    <Link to={`/request-assessment?package=${pkg.id}`} className={`${pkg.flagship ? "btn-primary" : "btn-ink"} mt-6 w-full px-3`} data-testid={`package-${pkg.id}-cta`}>Request quote</Link>
  </Reveal>
);

export const PricingGrid = ({ cur }) => (
  <div className="grid gap-px border border-line bg-line sm:grid-cols-2 xl:grid-cols-5" data-testid="pricing-grid">
    {packages.map((p, i) => <PriceCard key={p.id} pkg={p} cur={cur} index={i} />)}
  </div>
);

// ---------- Risk matrix ----------
const cellLevel = (l, i) => { const s = l * i; return s >= 20 ? "Critical" : s >= 12 ? "High" : s >= 6 ? "Medium" : s >= 3 ? "Low" : "Informational"; };
const cellColor = { Critical: "bg-red-700", High: "bg-orange-600", Medium: "bg-yellow-500", Low: "bg-green-600", Informational: "bg-slate-400" };
const demoCounts = { "5-5": 1, "4-4": 2, "3-4": 1, "3-3": 3, "2-3": 2, "2-2": 4, "1-2": 2 };

export const RiskMatrix = ({ dark = false }) => {
  const [hover, setHover] = useState(null);
  const label = hover ? `Likelihood ${hover[0]} × Impact ${hover[1]} → ${cellLevel(hover[0], hover[1])}` : "Hover or focus a cell";
  return (
    <figure className="max-w-[440px]" data-testid="risk-matrix">
      <div className="flex gap-3">
        <div className={`flex items-center font-mono text-[10px] uppercase tracking-[0.2em] [writing-mode:vertical-rl] rotate-180 ${dark ? "text-slate-400" : "text-slate-500"}`}>Likelihood →</div>
        <div className="grid flex-1 grid-cols-5 gap-1.5" role="grid" aria-label="Risk matrix, likelihood by impact">
          {[5, 4, 3, 2, 1].map((l) => [1, 2, 3, 4, 5].map((i) => {
            const lvl = cellLevel(l, i);
            const n = demoCounts[`${l}-${i}`];
            return (
              <button key={`${l}-${i}`} type="button" role="gridcell" aria-label={`Likelihood ${l}, impact ${i}: ${lvl}${n ? `, ${n} findings` : ""}`}
                onMouseEnter={() => setHover([l, i])} onFocus={() => setHover([l, i])} onMouseLeave={() => setHover(null)}
                className={`relative aspect-square ${cellColor[lvl]} transition-[opacity,transform] duration-200 hover:scale-[1.06] ${hover && (hover[0] !== l || hover[1] !== i) ? "opacity-50" : "opacity-90"}`}>
                {n && <span className="absolute inset-0 m-auto flex h-7 w-7 items-center justify-center rounded-full bg-white font-mono text-xs font-bold text-ink">{n}</span>}
              </button>
            );
          }))}
        </div>
      </div>
      <div className={`ml-7 mt-3 flex justify-between font-mono text-[10px] uppercase tracking-[0.2em] ${dark ? "text-slate-400" : "text-slate-500"}`}><span>Impact →</span><span aria-live="polite">{label}</span></div>
    </figure>
  );
};

export const SeverityTable = () => (
  <dl className="divide-y divide-line border-y border-line">
    {severity.map((s) => (
      <div key={s.level} className="grid gap-2 py-5 sm:grid-cols-[150px_1fr_200px] sm:gap-6">
        <dt className="flex items-center gap-2.5 font-bold"><span className={`h-2.5 w-2.5 ${cellColor[s.level]}`} aria-hidden />{s.level}</dt>
        <dd className="text-sm leading-relaxed text-slate-600">{s.def}</dd>
        <dd className="font-mono text-xs text-slate-500 sm:text-right">{s.sla}</dd>
      </div>
    ))}
  </dl>
);

// ---------- Country matrix ----------
const mark = { L: { t: "Typically relevant", c: "bg-teal text-white" }, C: { t: "Conditional", c: "bg-teal/15 text-teal" }, "—": { t: "Not a primary driver", c: "text-slate-400" } };
export const CountryMatrix = () => {
  const rows = useMemo(() => countries.map((c) => ({ c, v: matrix[c.slug] })), []);
  return (
    <div id="matrix" className="scroll-mt-28" data-testid="country-matrix">
      <div className="relative overflow-x-auto border border-line bg-white">
        <table className="w-full min-w-[860px] border-collapse text-left text-sm">
          <caption className="sr-only">Country applicability matrix</caption>
          <thead><tr className="border-b-2 border-ink">
            <th scope="col" className="px-4 py-4 font-semibold">Jurisdiction</th>
            {matrixTopics.map((t) => <th key={t} scope="col" className="px-3 py-4 text-xs font-semibold leading-tight">{t}</th>)}
          </tr></thead>
          <tbody>
            {rows.map(({ c, v }) => (
              <tr key={c.slug} className="border-b border-line hover:bg-paper">
                <th scope="row" className="px-4 py-3.5 font-semibold"><Link to={`/compliance/${c.slug}`} className="hover:text-teal">{c.name}</Link></th>
                {v.map((x, i) => <td key={i} className="px-3 py-3.5"><span title={mark[x].t} className={`inline-flex h-7 w-7 items-center justify-center rounded-sm font-mono text-xs font-bold ${mark[x].c}`}>{x === "—" ? <Minus className="h-3.5 w-3.5" aria-label={mark[x].t} /> : <><span aria-hidden>{x}</span><span className="sr-only">{mark[x].t}</span></>}</span></td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-xs text-slate-600">
        {Object.entries(mark).map(([k, m]) => <span key={k} className="inline-flex items-center gap-2"><span className={`inline-flex h-5 w-5 items-center justify-center rounded-sm font-mono text-[10px] font-bold ${m.c}`}>{k}</span>{m.t}</span>)}
        <span>Generalised guidance only — applicability depends on your facts. Not legal advice.</span>
      </p>
    </div>
  );
};

export const ServiceRow = ({ s, i }) => (
  <Reveal delay={(i % 4) * 0.04}>
    <Link to={`/services/${s.slug}`} className="group grid grid-cols-[48px_1fr_auto] items-start gap-4 border-b border-line py-6 transition-colors hover:bg-white sm:grid-cols-[72px_1fr_1.2fr_auto] sm:px-4" data-testid={`service-row-${s.slug}`}>
      <IconTile icon={serviceIcons[s.slug]} className="h-11 w-11" />
      <span className="text-lg font-bold leading-snug group-hover:text-teal">{s.title}</span>
      <span className="col-span-2 col-start-2 text-sm leading-relaxed text-slate-600 line-clamp-3 sm:col-span-1 sm:col-start-3">{s.short}</span>
      <ArrowRight className="row-start-1 col-start-3 h-5 w-5 text-slate-400 transition-transform duration-300 group-hover:translate-x-1 group-hover:text-teal sm:col-start-4" aria-hidden />
    </Link>
  </Reveal>
);
